import { CanvasDimensions, SpecialZoneType } from '../special-zones/types';
import { ImageResolution } from '../special-zones/utils';

export interface MediaLayout extends CanvasDimensions {
  top: number;
  left: number;
}

/** Image, video, or a canvas painted with decoded frames (FramePlayer). */
export type OverlayMediaElement =
  | HTMLImageElement
  | HTMLVideoElement
  | HTMLCanvasElement;

/** Intrinsic (content) resolution of the media, before object-fit layout. */
function getNaturalSize(
  mediaElement: OverlayMediaElement
): [width: number, height: number] {
  if (mediaElement instanceof HTMLVideoElement) {
    return [mediaElement.videoWidth, mediaElement.videoHeight];
  }
  if (mediaElement instanceof HTMLCanvasElement) {
    return [mediaElement.width, mediaElement.height];
  }
  return [mediaElement.naturalWidth, mediaElement.naturalHeight];
}

export interface MediaContentRect {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

export function getMediaContentRect(
  mediaElement: OverlayMediaElement,
  mediaRect: DOMRect
): MediaContentRect | null {
  const [naturalWidth, naturalHeight] = getNaturalSize(mediaElement);

  if (!naturalWidth || !naturalHeight || !mediaRect.width || !mediaRect.height)
    return null;

  const elementAspect = mediaRect.width / mediaRect.height;
  const contentAspect = naturalWidth / naturalHeight;

  if (getMediaObjectFit(mediaElement) === 'fill') {
    return {
      width: mediaRect.width,
      height: mediaRect.height,
      offsetX: 0,
      offsetY: 0,
    };
  }

  if (contentAspect > elementAspect) {
    const width = mediaRect.width;
    const height = mediaRect.width / contentAspect;
    return {
      width,
      height,
      offsetX: 0,
      offsetY: (mediaRect.height - height) / 2,
    };
  }

  const height = mediaRect.height;
  const width = mediaRect.height * contentAspect;
  return { width, height, offsetX: (mediaRect.width - width) / 2, offsetY: 0 };
}

export function getMediaLayout(
  mediaElement: OverlayMediaElement,
  overlayRoot: HTMLDivElement
): MediaLayout | null {
  const overlayContainer = overlayRoot.parentElement;
  if (!overlayContainer) return null;

  const containerRect = overlayContainer.getBoundingClientRect();
  const mediaRect = mediaElement.getBoundingClientRect();
  const contentRect = getMediaContentRect(mediaElement, mediaRect);
  if (!contentRect) return null;

  return {
    top: mediaRect.top - containerRect.top + contentRect.offsetY,
    left: mediaRect.left - containerRect.left + contentRect.offsetX,
    width: contentRect.width,
    height: contentRect.height,
  };
}

function getMediaObjectFit(mediaElement: OverlayMediaElement): string {
  return window.getComputedStyle(mediaElement).objectFit || 'fill';
}

export function getMediaResolution(
  mediaElement: OverlayMediaElement
): ImageResolution | null {
  const [width, height] = getNaturalSize(mediaElement);
  return width && height ? { width, height } : null;
}

export function isSpecialZoneType(value?: string): value is SpecialZoneType {
  return (
    value === SpecialZoneType.DeadZone ||
    value === SpecialZoneType.VehicleParkingZone ||
    value === SpecialZoneType.ActiveZone
  );
}
